/**
 * Lock-on (ท่าล็อกเป้า, docs/systems/SKILLS_V2.md 1.2): the user's "hold to lock the foe, then perform the form".
 *
 * - A `skill` command with `hold: true` (the client sends it once the key has been held `AIM.holdS`) on a `lock` or
 *   `volley` move locks the target: the current target if it is a hostile in reach, else the nearest. The lock is
 *   confirmed when the move starts (`castStart` / `skill`); a move that does not start leaves no lock.
 * - A tap (no `hold`) with no target in reach takes the nearest hostile in the `AIM.tapConeDeg` cone ahead.
 * - While locked the caster faces the target. A dodge makes the blow in flight whiff but the lock holds. Farther
 *   than range + `AIM.dropSlack`, dead or gone for `AIM.dropGrace` s drops the lock, and the move's cooldown then
 *   runs at `AIM.dropCooldown` of its full time. A stun ends it at once. Otherwise it ends with the form
 *   (`lockFormS`) or the next move.
 * - Volleys read `lockedId()` (world.ts): a held volley sends every projectile to the locked foe.
 * - Events: `lock` with the target id, and `tgtId: null` when it ends (`dropped` when lost).
 */
import { AIM, aimOf, inCone, lockFormS } from '../data/skillAim';
import { SKILLS } from '../data/skills';
import type { Entity, PlayerCommand, WorldEvent } from '../world';
import { duelLockOf } from './duelLock';
import { skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

export type LockEvent = { type: 'lock'; srcId: number; tgtId: number | null; skillId: string; dropped?: boolean };

interface Lock {
  tgtId: number;
  skillId: string;
  /** Seconds of form left. */
  formT: number;
  /** Seconds the target has been out of reach. */
  graceT: number;
}

const dist = (a: Entity, b: Entity) => Math.hypot(a.x - b.x, a.y - b.y);

export class LockOnSystem implements WorldSystem {
  readonly name = 'lockOn';
  private lock: Lock | null = null;
  /** Asked for by a held command, waiting for the move to start this tick. */
  private asked: { tgtId: number; skillId: string } | null = null;

  constructor(private readonly h: SystemHost) {}

  /** The player's locked foe (or the one being locked this tick), or null. */
  lockedId(): number | null {
    return this.lock?.tgtId ?? this.asked?.tgtId ?? duelLockOf(this.h.world)?.lockedId() ?? null;
  }

  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'skill') return false;
    const w = this.h.world;
    const p = w.player;
    const id = skillBarOf(w.progress)[cmd.slot];
    const s = id ? SKILLS[id] : undefined;
    if (!s || p.dead) return false;
    // A new move ends the old form's lock.
    if (this.lock) this.end(false);
    const aim = aimOf(s);
    if (aim !== 'lock' && aim !== 'volley') return false;
    const reach = s.range + 0.4;
    const ok = (e: Entity | undefined): e is Entity =>
      !!e && !e.dead && e !== p && w.isHostile(p, e) && dist(p, e) <= reach;
    let tgt: Entity | undefined = ok(w.get(p.targetId)) ? w.get(p.targetId) : undefined;
    if (!tgt) {
      let best = Infinity;
      for (const e of w.entities.values()) {
        if (!ok(e)) continue;
        if (!cmd.hold && !inCone(p, e)) continue;
        const d = dist(p, e);
        if (d < best) [best, tgt] = [d, e];
      }
    }
    if (!tgt) return false;
    p.targetId = tgt.id;
    if (cmd.hold) this.asked = { tgtId: tgt.id, skillId: s.id };
    return false;
  }

  observe(e: WorldEvent): void {
    const a = this.asked;
    if (!a || (e.type !== 'castStart' && e.type !== 'skill')) return;
    if (e.srcId !== this.h.world.player.id || e.skillId !== a.skillId) return;
    this.asked = null;
    this.lock = { tgtId: a.tgtId, skillId: a.skillId, formT: lockFormS(SKILLS[a.skillId]!), graceT: 0 };
    this.h.emit({ type: 'lock', srcId: e.srcId, tgtId: a.tgtId, skillId: a.skillId });
  }

  step(dt: number): void {
    this.asked = null; // a held move that did not start locks nothing
    const l = this.lock;
    if (!l) return;
    const w = this.h.world;
    const p = w.player;
    if (p.dead || p.stunT > 0) return this.end(false);
    l.formT -= dt;
    if (l.formT <= 0) return this.end(false);
    const tgt = w.get(l.tgtId);
    const s = SKILLS[l.skillId]!;
    if (!tgt || tgt.dead || dist(p, tgt) > s.range + AIM.dropSlack) {
      l.graceT += dt;
      if (l.graceT >= AIM.dropGrace - 1e-9) this.end(true);
      return;
    }
    l.graceT = 0;
    this.h.face(p, tgt);
  }

  private end(dropped: boolean): void {
    const l = this.lock;
    if (!l) return;
    this.lock = null;
    const w = this.h.world;
    const p = w.player;
    if (dropped) {
      const full = SKILLS[l.skillId]!.cooldown;
      if (p.cooldowns[l.skillId]) p.cooldowns[l.skillId] = Math.min(p.cooldowns[l.skillId]!, full * AIM.dropCooldown);
    }
    this.h.emit({ type: 'lock', srcId: p.id, tgtId: null, skillId: l.skillId, ...(dropped ? { dropped } : {}) });
  }
}
